import React from 'react';
import './Guides.css';

export default function MotorGuide() {
  return (
    <div className="guide-container">
      <h1 className="guide-header">⚙️ How to Connect a Motor + Code It</h1>

      <div className="guide-body">
        <h2>⚙️ DC Motor Control with Arduino</h2>
        <p>
          In this guide, you'll learn how to connect a simple DC motor to your Arduino and control it using code. 
          Motors are the heart of any moving robot, so mastering this skill is essential for robotics!
        </p>

        <h2>🧰 What You'll Need:</h2>
        <ul className="components-list">
          <li>Arduino Uno or Nano</li>
          <li>TT DC Motor (yellow gear motor)</li>
          <li>L298N Motor Driver Module</li>
          <li>External battery pack (6V or 9V recommended)</li>
          <li>Jumper wires (male-to-male and male-to-female)</li>
          <li>Breadboard (optional for connections)</li>
        </ul>

        <div className="info-box">
          <p>💡 <strong>Why do we need a motor driver?</strong> Arduino pins can only provide about 20mA of current, but motors need much more power (100mA-2A). The L298N acts as a bridge, letting the Arduino control high-power devices safely.</p>
        </div>

        <h2>🔌 Wiring Diagram:</h2>
        <div className="wiring-steps">
          <h3>Motor Driver to Arduino:</h3>
          <ul>
            <li><strong>L298N ENA</strong> → Arduino <strong>pin 9</strong> (PWM for speed control)</li>
            <li><strong>L298N IN1</strong> → Arduino <strong>pin 8</strong></li>
            <li><strong>L298N IN2</strong> → Arduino <strong>pin 7</strong></li>
            <li><strong>L298N GND</strong> → Arduino <strong>GND</strong></li>
          </ul>
          
          <h3>Power Connections:</h3>
          <ul>
            <li><strong>Battery +</strong> → L298N <strong>VCC</strong> (motor power)</li>
            <li><strong>Battery -</strong> → L298N <strong>GND</strong></li>
            <li><strong>L298N 5V Out</strong> → Arduino <strong>VIN</strong> (if using 9V battery)</li>
          </ul>

          <h3>Motor Connections:</h3>
          <ul>
            <li><strong>Motor wire 1</strong> → L298N <strong>OUT1</strong></li>
            <li><strong>Motor wire 2</strong> → L298N <strong>OUT2</strong></li>
          </ul>
        </div>

        <div className="warning-box">
          <p>⚠️ <strong>Safety First:</strong> Always connect power last and disconnect first. Make sure your battery voltage doesn't exceed the motor driver's rating (up to 12V for L298N).</p>
        </div>

        <h2>💻 Basic Motor Control Code:</h2>
        <div className="code-block">
          <pre>
{`// Motor control pins
int motorEnable = 9;   // PWM pin for speed
int motorIn1 = 8;      // Direction pin 1
int motorIn2 = 7;      // Direction pin 2

void setup() {
  // Set motor pins as outputs
  pinMode(motorEnable, OUTPUT);
  pinMode(motorIn1, OUTPUT);
  pinMode(motorIn2, OUTPUT);
  
  Serial.begin(9600);
  Serial.println("Motor Control Ready!");
}

void loop() {
  // Forward motion
  Serial.println("Moving Forward");
  digitalWrite(motorIn1, HIGH);
  digitalWrite(motorIn2, LOW);
  analogWrite(motorEnable, 255);  // Full speed
  delay(2000);
  
  // Stop
  Serial.println("Stopping");
  digitalWrite(motorIn1, LOW);
  digitalWrite(motorIn2, LOW);
  delay(1000);
  
  // Backward motion
  Serial.println("Moving Backward");
  digitalWrite(motorIn1, LOW);
  digitalWrite(motorIn2, HIGH);
  analogWrite(motorEnable, 255);  // Full speed
  delay(2000);
  
  // Stop
  Serial.println("Stopping");
  digitalWrite(motorIn1, LOW);
  digitalWrite(motorIn2, LOW);
  delay(1000);
}`}
          </pre>
        </div>

        <h2>🎛️ Advanced: Speed Control</h2>
        <p>Use PWM (Pulse Width Modulation) to control motor speed by changing the value sent to the enable pin:</p>
        
        <div className="code-block">
          <pre>
{`// Speed control examples
analogWrite(motorEnable, 100);  // Slow speed (~40%)
analogWrite(motorEnable, 180);  // Medium speed (~70%)
analogWrite(motorEnable, 255);  // Full speed (100%)`}
          </pre>
        </div>

        <h2>🧪 Understanding the Code:</h2>
        <div className="explanation-box">
          <ul>
            <li><strong>IN1 HIGH, IN2 LOW:</strong> Motor spins forward</li>
            <li><strong>IN1 LOW, IN2 HIGH:</strong> Motor spins backward</li>
            <li><strong>Both LOW:</strong> Motor stops (coast)</li>
            <li><strong>Both HIGH:</strong> Motor brakes (not recommended)</li>
            <li><strong>analogWrite(ENA, value):</strong> Controls speed (0-255)</li>
          </ul>
        </div>

        <h2>🔧 Troubleshooting:</h2>
        <div className="troubleshoot-box">
          <ul>
            <li><strong>Motor not spinning:</strong> Check power connections and battery charge</li>
            <li><strong>Motor spinning wrong direction:</strong> Swap the motor wires on OUT1 and OUT2</li>
            <li><strong>Erratic behavior:</strong> Ensure common ground between Arduino and motor driver</li>
            <li><strong>Slow/weak movement:</strong> Check if battery voltage is sufficient (6-9V recommended)</li>
          </ul>
        </div>

        <div className="success-box">
          <p>🎉 <strong>Great job!</strong> You've successfully controlled a DC motor with Arduino. This is a fundamental skill for building moving robots, cars, and many other projects!</p>
        </div>

        <h2>🚀 Next Steps:</h2>
        <p>Ready to level up? Try these exciting modifications:</p>
        <ul>
          <li>Control two motors to build a simple robot car</li>
          <li>Add sensors to make the motor respond to light or distance</li>
          <li>Use a potentiometer to manually control motor speed</li>
          <li>Build a line-following robot using motor control</li>
        </ul>
      </div>
    </div>
  );
}